import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { QboTargetPicker } from './QboTargetPicker';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const LIST_TYPE_LABEL = { customer: 'Customer', item: 'Item', class: 'Class', location: 'Location', tax_code: 'VAT code' };

// Safe, job-scoped Sandbox reference mapping — re-maps ONLY the exact
// customer/item(s)/class/location THIS job needs against the real,
// currently-connected Sandbox company, so a genuine end-to-end
// create/readback test becomes possible. Never auto-applies a pick;
// reuses the same QboTargetPicker as Region verification.
export default function QboSandboxMapDialog({ jobId, onClose, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({}); // "listType:name" -> {id, name}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/sales-invoicing/jobs/${jobId}/qbo-live/sandbox-map`, { headers: authHeaders() });
      setData(data);
      setPicked({});
    } catch (err) {
      toast.error(getApiError(err, 'Could not fetch this job\'s Sandbox references.'));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const rowKey = (row) => `${row.list_type}:${row.name}`;
  const rowDisplayName = (row) => (row.list_type === 'tax_code' ? 'Standard VAT rate (every line)' : row.name);

  // Never auto-applies: only a pick the human has actually clicked in the
  // picker is ever included in Save — a displayed suggestion is a hint,
  // not a selection, same as everywhere else in this workspace.
  const choiceFor = (row) => picked[rowKey(row)] || null;

  const applyAll = async () => {
    const choices = data.rows
      .map((row) => {
        const choice = choiceFor(row);
        return choice ? { list_type: row.list_type, name: row.name, qbo_id: choice.id } : null;
      })
      .filter(Boolean);
    if (choices.length === 0) {
      toast.error('Pick at least one Sandbox target first.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/jobs/${jobId}/qbo-live/sandbox-map`, { choices }, { headers: authHeaders() });
      toast.success(`Mapped ${choices.length} reference(s) to the connected Sandbox.`);
      await load();
      onApplied?.();
    } catch (err) {
      toast.error(getApiError(err, 'Could not save that mapping.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="qbo-sandbox-map-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">Map to Sandbox references — {data?.hotel_name || 'Loading…'}</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500" data-testid="qbo-sandbox-map-loading">Fetching this job's live Sandbox candidates…</div>}

        {!loading && data && (
          <div className="space-y-4 min-w-0" data-testid="qbo-sandbox-map-content">
            <div className="text-xs text-slate-500">
              Connected Sandbox company {data.realm_id} ({data.environment}). Nothing is applied automatically — pick each
              reference's real Sandbox target yourself, then Save. This also fixes every other job already using the same name.
            </div>

            {data.rows.length === 0 && (
              <div className="text-sm text-slate-500" data-testid="qbo-sandbox-map-empty">Nothing for this job needs mapping.</div>
            )}

            {data.rows.map((row) => {
              const choice = picked[rowKey(row)];
              return (
                <div key={rowKey(row)} className="border rounded-lg p-3 space-y-2 min-w-0" data-testid={`qbo-sandbox-map-row-${row.list_type}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <span className="font-medium text-sm flex items-center gap-2 min-w-0 flex-1">
                      <Badge variant="outline" className="shrink-0">{LIST_TYPE_LABEL[row.list_type]}</Badge>
                      <span className="truncate" title={rowDisplayName(row)}>{rowDisplayName(row)}</span>
                    </span>
                    {row.found_in_connected_realm ? (
                      <Badge className="bg-emerald-100 text-emerald-800 shrink-0 whitespace-nowrap" data-testid={`qbo-sandbox-map-${row.list_type}-found-badge`}><CheckCircle2 className="h-3 w-3 mr-1" />Confirmed for this Sandbox</Badge>
                    ) : row.ambiguous ? (
                      <Badge variant="destructive" className="shrink-0 whitespace-nowrap" data-testid={`qbo-sandbox-map-${row.list_type}-ambiguous-badge`}><AlertTriangle className="h-3 w-3 mr-1" />Ambiguous — pick one</Badge>
                    ) : !row.suggestion ? (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 shrink-0 whitespace-nowrap" data-testid={`qbo-sandbox-map-${row.list_type}-missing-badge`}><HelpCircle className="h-3 w-3 mr-1" />Not confirmed</Badge>
                    ) : (
                      <Badge variant="secondary" className="shrink-0 whitespace-nowrap" data-testid={`qbo-sandbox-map-${row.list_type}-suggested-badge`}>Suggested ({Math.round(row.suggestion.confidence * 100)}%)</Badge>
                    )}
                  </div>

                  {!row.found_in_connected_realm && row.current_external_code && (
                    <div className="text-xs text-slate-500 break-words" data-testid={`qbo-sandbox-map-${row.list_type}-mismatch-note`}>
                      Currently mapped to QBO ID {row.current_external_code}
                      {row.current_external_code_live_name ? ` → live Sandbox entity "${row.current_external_code_live_name}"` : ' (not found live in the connected Sandbox)'}
                      {' — not yet confirmed as correct for this Sandbox company.'}
                    </div>
                  )}

                  {row.ambiguous && (
                    <div className="text-xs text-red-700 break-words" data-testid={`qbo-sandbox-map-${row.list_type}-tied-list`}>
                      Tied: {row.tied_candidates.map((c) => c.name).join(', ')} — select the right one below.
                    </div>
                  )}

                  {!row.found_in_connected_realm && !row.ambiguous && row.suggestion && !choice && (
                    <div className="text-xs text-slate-500" data-testid={`qbo-sandbox-map-${row.list_type}-suggestion-hint`}>
                      Suggested: "{row.suggestion.name}" ({Math.round(row.suggestion.confidence * 100)}% match) — pick it below to confirm, nothing is applied until you do.
                    </div>
                  )}

                  {!row.found_in_connected_realm && (
                    <QboTargetPicker
                      candidates={row.candidates}
                      disabled={saving}
                      selectedName={choice?.name || null}
                      onPick={(id, name) => setPicked((p) => ({ ...p, [rowKey(row)]: { id, name } }))}
                      testKey={`qbo-sandbox-map-${row.list_type}`}
                    />
                  )}
                </div>
              );
            })}

            {data.rows.some((r) => !r.found_in_connected_realm) && (
              <Button className="w-full" disabled={saving} onClick={applyAll} data-testid="qbo-sandbox-map-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Save mapping
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
